import { useTranslation } from 'react-i18next';
import i18n from '@/i18n';

export default function Problem() {
  const { t } = useTranslation();

  return (
    <section id="problem" className="pb-16 md:pb-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Hero banner — negative margin only on md+ to achieve full-width effect */}
        <div className="mb-12 md:mb-20 relative md:-mx-8 overflow-hidden rounded-lg md:rounded-none">
          <img
            src="https://public.readdy.ai/ai/img_res/4bebe4b671750448c775afc16799fdbd.jpg"
            alt="Traditional Grilling Problems"
            className="w-full h-52 md:h-96 object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/35 to-black/55"></div>
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4 md:px-8">
            <h2
              className="text-xl md:text-4xl lg:text-5xl font-bold text-white mb-3 md:mb-4 leading-tight"
              style={{ fontFamily: 'Noto Sans KR, sans-serif', wordBreak: 'keep-all' }}
            >
              {t('problem_hero_title')}
            </h2>
            <div className="w-10 md:w-12 h-1 bg-teal-400 mx-auto mb-3 md:mb-4"></div>
            <p
              className="text-xs md:text-base lg:text-xl text-white/85 leading-relaxed max-w-2xl whitespace-pre-line"
              style={{ fontFamily: 'Noto Sans KR, sans-serif', wordBreak: 'keep-all' }}
            >
              {t('problem_hero_sub')}
            </p>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-8 md:gap-12 mb-12 md:mb-20">
          {/* Col 1: Problem */}
          <div className="space-y-5 md:space-y-8">
            <h3
              className="text-xl md:text-3xl font-bold text-gray-900"
              style={{ fontFamily: 'Noto Sans KR, sans-serif' }}
            >
              {t('problem_col1_title')}
            </h3>
            <p
              className="text-base md:text-2xl lg:text-3xl font-semibold text-gray-800 leading-relaxed bg-gray-200 px-4 py-3 rounded-lg"
              style={{ fontFamily: 'Noto Sans KR, sans-serif', wordBreak: 'keep-all' }}
            >
              {t('problem_col1_summary')}
            </p>
            <p
              className="text-sm md:text-lg lg:text-2xl text-gray-700 leading-relaxed whitespace-pre-line"
              style={{ fontFamily: 'Noto Sans KR, sans-serif' }}
            >
              {t('problem_col1_detail')}
            </p>
          </div>

          {/* Col 2: Solution */}
          <div className="space-y-5 md:space-y-8">
            <h3
              className="text-xl md:text-3xl font-bold text-gray-900"
              style={{ fontFamily: 'Noto Sans KR, sans-serif' }}
            >
              {t('problem_col2_title')}
            </h3>
            <p
              className="text-base md:text-2xl lg:text-3xl font-semibold text-white leading-relaxed bg-teal-500 px-4 py-3 rounded-lg"
              style={{ fontFamily: 'Noto Sans KR, sans-serif', wordBreak: 'keep-all' }}
            >
              {t('problem_col2_summary')}
            </p>
            <p
              className="text-sm md:text-lg lg:text-2xl text-gray-700 leading-relaxed whitespace-pre-line"
              style={{ fontFamily: 'Noto Sans KR, sans-serif' }}
            >
              {t('problem_col2_detail')}
            </p>
          </div>

          {/* Col 3: Impact */}
          <div className="space-y-5 md:space-y-8">
            <h3
              className="text-xl md:text-3xl font-bold text-gray-900"
              style={{ fontFamily: 'Noto Sans KR, sans-serif' }}
            >
              {t('problem_col3_title')}
            </h3>
            <p
              className="text-base md:text-2xl lg:text-3xl font-semibold text-white leading-relaxed bg-red-500 px-4 py-3 rounded-lg whitespace-pre-line"
              style={{ fontFamily: 'Noto Sans KR, sans-serif', wordBreak: 'keep-all' }}
            >
              {t('problem_col3_summary')}
            </p>
            <ul className="space-y-2 md:space-y-3">
              {t('problem_col3_list').split('\n').map((item) => (
                <li
                  key={item}
                  className="flex items-start gap-2 text-sm md:text-lg lg:text-2xl text-gray-700 leading-relaxed"
                  style={{ fontFamily: 'Noto Sans KR, sans-serif' }}
                >
                  {i18n.language !== 'ko' && (
                    <span className="mt-1 md:mt-1.5 w-2 h-2 md:w-2.5 md:h-2.5 rounded-full bg-red-500 flex-shrink-0" />
                  )}
                  <span>{item}</span>
                </li>
              ))}
            </ul>
            <div className="space-y-1">
              <p className="text-sm md:text-xl lg:text-2xl font-semibold text-gray-800" style={{ fontFamily: 'Noto Sans KR, sans-serif' }}>
                {t('problem_col3_production_label')}
              </p>
              <p className="text-sm md:text-lg lg:text-2xl text-gray-700 leading-relaxed whitespace-pre-line" style={{ fontFamily: 'Noto Sans KR, sans-serif' }}>
                {t('problem_col3_production')}
              </p>
            </div>
            <p className="text-sm md:text-lg lg:text-2xl text-gray-700 leading-relaxed whitespace-pre-line" style={{ fontFamily: 'Noto Sans KR, sans-serif', wordBreak: 'keep-all' }}>
              {t('problem_col3_closing')}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
